import { Link } from 'react-router-dom';
import { Badge, Box, Button, Card, Group, List, Stack, Text, Title } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

import type { BillingCycle, Plan } from './plans';
import classes from './PlanCard.module.css';

interface PlanCardProps {
  plan: Plan;
  cycle: BillingCycle;
}

export function PlanCard({ plan, cycle }: PlanCardProps) {
  const price = cycle === 'annual' ? plan.priceAnnual / 12 : plan.priceMonthly;
  const priceLabel = price === 0 ? 'Free' : `$${price.toFixed(price % 1 === 0 ? 0 : 2)}`;
  const periodLabel = price === 0 ? '' : cycle === 'annual' ? '/mo, billed yearly' : '/month';

  return (
    <Card
      className={`${classes.card} ${plan.highlight ? classes.highlighted : ''}`}
      p="xl"
      h="100%"
      withBorder
      radius="lg"
    >
      {plan.badge && (
        <Badge
          className={classes.badge}
          variant="gradient"
          gradient={{ from: 'var(--tf-forge-start)', to: 'var(--tf-forge-end)', deg: 135 }}
          size="sm"
        >
          {plan.badge}
        </Badge>
      )}

      <Stack gap="md" h="100%">
        <Box>
          <Title order={3} fz="h4" c="var(--tf-text-primary)">
            {plan.name}
          </Title>
          <Text c="var(--tf-text-secondary)" size="sm" mt={4}>
            {plan.tagline}
          </Text>
        </Box>

        <Group align="baseline" gap={4}>
          <Text className={classes.price}>{priceLabel}</Text>
          {periodLabel && (
            <Text c="var(--tf-text-secondary)" size="sm">
              {periodLabel}
            </Text>
          )}
        </Group>

        <Button
          component={Link}
          to={plan.cta.to}
          size="md"
          radius="md"
          variant={plan.highlight ? 'gradient' : 'default'}
          gradient={
            plan.highlight
              ? { from: 'var(--tf-forge-start)', to: 'var(--tf-forge-end)', deg: 135 }
              : undefined
          }
          fullWidth
        >
          {plan.cta.label}
        </Button>

        <List
          icon={
            <Box className={classes.checkIcon} aria-hidden="true">
              <IconCheck size={14} stroke={3} />
            </Box>
          }
          spacing="xs"
          size="sm"
          c="var(--tf-text-secondary)"
        >
          {plan.features.map((f) => (
            <List.Item key={f}>{f}</List.Item>
          ))}
        </List>
      </Stack>
    </Card>
  );
}
